import { z } from 'zod';

import { opsRunStatusSchema } from './ingest.js';

/**
 * DTOs de lectura de la consola (Overview, Analítica, Historial, Operaciones, Alertas) y de la
 * config de ingesta (conexiones de Instantly, API keys). Todo respeta el alcance X-Scope.
 */

const isoDate = z.string();
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Rango en días que acepta la consola (default 30). */
export const rangeQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(180).default(30),
});
export type RangeQuery = z.input<typeof rangeQuerySchema>;

// ------------------------------------------------------------------ Overview

export const funnelStepSchema = z.object({ key: z.string(), label: z.string(), value: z.number() });

/**
 * ¿Se miden aperturas (o clics) en el alcance? Con el seguimiento apagado Instantly NO registra
 * nada: mostrar 0 % sería mentir. `all` = todas las campañas con envíos lo miden desde antes del
 * rango; `partial` = sólo algunas, o desde una fecha dentro del rango (`since`); `none` = ninguna.
 *
 * `reason` distingue, cuando no hay una tasa real que mostrar, POR QUÉ: `tracking_off` (el
 * seguimiento está apagado — siempre junto a `status: 'none'`) vs. `no_tracked_sends` (el
 * seguimiento está prendido pero todavía no salió ningún envío bajo esa ventana, así que la tasa
 * da `null` sin que sea un "no se mide"). `null` = hay una tasa real que mostrar. Campo nuevo,
 * opcional para no romper a quien ya consume `status`/`since` sin mirarlo.
 */
export const trackingReasonSchema = z.enum(['tracking_off', 'no_tracked_sends']);
export type TrackingReason = z.infer<typeof trackingReasonSchema>;

export const trackingStatusSchema = z.object({
  status: z.enum(['all', 'partial', 'none']),
  /**
   * Primer día (UTC) desde el que cuentan las tasas, si es posterior al inicio del rango. Con
   * `reason: 'no_tracked_sends'` es en cambio la fecha real en que se prendió el seguimiento (para
   * el aviso "activo desde…"), no el día desde el que cuenta la tasa.
   */
  since: dateOnly.nullable(),
  reason: trackingReasonSchema.nullable().optional(),
});
export type TrackingStatus = z.infer<typeof trackingStatusSchema>;

/**
 * Los números de cabecera de una campaña en el panel de Instantly (totales desde que empezó),
 * sumados para el alcance. Las tasas de apertura/clic sólo cuentan lo enviado con seguimiento.
 */
export const campaignTotalsSchema = z.object({
  /** "Secuencias iniciadas" (contacted_count). */
  sequencesStarted: z.number(),
  sent: z.number(),
  opens: z.number(),
  openRate: z.number().nullable(),
  openTracking: trackingStatusSchema,
  clicks: z.number(),
  clickRate: z.number().nullable(),
  clickTracking: trackingStatusSchema,
  replies: z.number(),
  replyRate: z.number().nullable(),
  opportunities: z.number(),
  opportunityValue: z.number(),
  /**
   * Leads marcados «ganado» en Instantly (total_closed). Casi siempre 0: nadie los marca a mano, por
   * eso la consola sólo lo muestra como nota cuando hay alguno.
   */
  conversions: z.number(),
  /**
   * «Pidieron información» (2026-10-08): leads DISTINTOS del período que respondieron interesados
   * (Instantly `lead_interested`/reunión/ganado o `interest_status` 1-4) o que recibieron el
   * dossier del primer contacto, sin los marcados después como no interesados (estado < 0).
   * A diferencia del resto de la cabecera, cuenta el período elegido (por fecha del evento).
   */
  infoRequested: z.number(),
  /** Moneda de los valores de Instantly (USD salvo que se configure otra). */
  currency: z.string(),
  /** Última actualización desde Instantly (cada hora). */
  updatedAt: isoDate.nullable(),
});
export type CampaignTotals = z.infer<typeof campaignTotalsSchema>;

export const abVariantSchema = z.object({
  /** "A", "B", "C"… (la variante 0 de Instantly es la A). */
  label: z.string(),
  subject: z.string().nullable(),
  bodyPreview: z.string().nullable(),
  sent: z.number(),
  opens: z.number(),
  /** Sobre lo enviado con seguimiento; `null` = no se mide. */
  openRate: z.number().nullable(),
  replies: z.number(),
  replyRate: z.number().nullable(),
  clicks: z.number().nullable(),
  opportunities: z.number(),
});
export type AbVariant = z.infer<typeof abVariantSchema>;

export const abTestSchema = z.object({
  campaignId: z.uuid(),
  campaignName: z.string(),
  tenantSlug: z.string(),
  /** Paso de la secuencia, desde 1. */
  step: z.number(),
  variants: z.array(abVariantSchema),
  verdict: z.object({
    status: z.enum(['leader', 'insufficient', 'tie']),
    leader: z.string().nullable(),
    message: z.string(),
  }),
  /** Seguimiento de la campaña dueña del paso (igual para todos los pasos de la misma campaña). */
  openTracking: trackingStatusSchema,
  clickTracking: trackingStatusSchema,
});
export type AbTest = z.infer<typeof abTestSchema>;

export const overviewResponseSchema = z.object({
  range: z.object({ from: dateOnly, to: dateOnly, days: z.number() }),
  kpis: z.object({
    sentToday: z.number(),
    sent7d: z.number(),
    sentRange: z.number(),
    /**
     * Tasas sobre el rango: únicos / enviados (0..1). `null` si no hubo envíos. La de apertura sólo
     * cuenta envíos de campañas con seguimiento (desde que se prendió): ver `openTracking`.
     */
    openRate: z.number().nullable(),
    openTracking: trackingStatusSchema,
    replyRate: z.number().nullable(),
    bounceRate: z.number().nullable(),
    interested: z.number(),
    leadsQueued: z.number(),
    openAlerts: z.number(),
  }),
  funnel: z.array(funnelStepSchema),
  /** Cabecera "como en Instantly" (totales de las campañas del alcance). */
  instantly: campaignTotalsSchema,
  trend: z.array(
    z.object({
      date: dateOnly,
      sent: z.number(),
      opened: z.number(),
      replied: z.number(),
      bounced: z.number(),
    }),
  ),
  byTenant: z.array(
    z.object({
      tenantId: z.uuid(),
      slug: z.string(),
      name: z.string(),
      sent: z.number(),
      opened: z.number(),
      replied: z.number(),
      bounced: z.number(),
      leads: z.number(),
    }),
  ),
});
export type OverviewResponse = z.infer<typeof overviewResponseSchema>;

// ------------------------------------------------------------------ Analítica

export const analyticsDailyResponseSchema = z.object({
  range: z.object({ from: dateOnly, to: dateOnly, days: z.number() }),
  /** Números oficiales de Instantly (`campaigns/analytics/daily`), sumados por día. */
  daily: z.array(
    z.object({
      date: dateOnly,
      sent: z.number(),
      uniqueOpened: z.number(),
      uniqueReplies: z.number(),
      repliesAutomatic: z.number(),
      uniqueClicks: z.number(),
      opportunities: z.number(),
      /** Instantly no da rebotes por día y campaña: se derivan de los eventos `email_bounced`. */
      bounced: z.number(),
    }),
  ),
  campaigns: z.array(
    z.object({
      campaignId: z.uuid(),
      externalId: z.string(),
      name: z.string(),
      tenantSlug: z.string(),
      sent: z.number(),
      uniqueOpened: z.number(),
      uniqueReplies: z.number(),
      uniqueClicks: z.number(),
      opportunities: z.number(),
      bounced: z.number(),
      /** Totales de la campaña como los muestra Instantly (desde que empezó). */
      totals: campaignTotalsSchema,
    }),
  ),
  /** Pruebas A/B por campaña y paso de la secuencia. */
  abTests: z.array(abTestSchema),
  /** ¿Alguna campaña del alcance mide clics? Si no, la consola no muestra clics en ningún lado. */
  clickTracking: trackingStatusSchema,
  openTracking: trackingStatusSchema,
});
export type AnalyticsDailyResponse = z.infer<typeof analyticsDailyResponseSchema>;

export const stepMetricsResponseSchema = z.array(
  z.object({
    campaignId: z.uuid(),
    campaignName: z.string(),
    tenantSlug: z.string(),
    periodStart: dateOnly,
    periodEnd: dateOnly,
    step: z.string(),
    variant: z.string(),
    sent: z.number(),
    uniqueOpened: z.number(),
    uniqueReplies: z.number(),
    uniqueClicks: z.number(),
    opportunities: z.number(),
  }),
);
export type StepMetricsResponse = z.infer<typeof stepMetricsResponseSchema>;

export const mailboxMetricsResponseSchema = z.array(
  z.object({
    mailbox: z.string(),
    tenantSlug: z.string(),
    status: z.number().nullable(),
    warmupScore: z.number().nullable(),
    dailyLimit: z.number().nullable(),
    sent: z.number(),
    bounced: z.number(),
    uniqueOpened: z.number(),
    uniqueReplies: z.number(),
  }),
);
export type MailboxMetricsResponse = z.infer<typeof mailboxMetricsResponseSchema>;

// ------------------------------------------------------------------ Historial

export const historySearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(200),
});

export const historySearchResponseSchema = z.array(
  z.object({
    email: z.string(),
    company: z.string().nullable(),
    domain: z.string().nullable(),
    tenantSlug: z.string(),
    tenantName: z.string(),
    leadId: z.uuid().nullable(),
    lastActivityAt: isoDate.nullable(),
    messages: z.number(),
  }),
);
export type HistorySearchResponse = z.infer<typeof historySearchResponseSchema>;

export const timelineQuerySchema = z.object({ email: z.string().trim().min(3).max(320) });

export const attachmentDtoSchema = z.object({
  id: z.uuid(),
  filename: z.string(),
  mime: z.string(),
  sizeBytes: z.number(),
  sha256: z.string(),
});
export type AttachmentDto = z.infer<typeof attachmentDtoSchema>;

export const timelineItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('message'),
    id: z.uuid(),
    at: isoDate,
    tenantSlug: z.string(),
    channel: z.enum(['instantly', 'gmail', 'manual']),
    direction: z.enum(['out', 'in']),
    status: z.string(),
    subject: z.string().nullable(),
    bodyHtml: z.string().nullable(),
    bodyText: z.string().nullable(),
    fromEmail: z.string().nullable(),
    toEmail: z.string().nullable(),
    mailbox: z.string().nullable(),
    campaignName: z.string().nullable(),
    step: z.number().nullable(),
    variant: z.number().nullable(),
    /** Enviado sin píxel de apertura (campaña sin seguimiento en ese momento). `null` = no aplica. */
    untracked: z.boolean().nullable().optional(),
    isAutoReply: z.boolean(),
    /**
     * Quién lo escribió (sólo salientes): `campaign` paso de la secuencia, `bot` el asistente
     * (respuesta o dossier), `manual` una persona (Unibox a mano, Gmail). `null` = no se sabe.
     */
    origin: z.enum(['campaign', 'bot', 'manual']).nullable().optional(),
    attachments: z.array(attachmentDtoSchema),
  }),
  z.object({
    kind: z.literal('event'),
    id: z.uuid(),
    at: isoDate,
    tenantSlug: z.string(),
    eventType: z.string(),
    campaignName: z.string().nullable(),
    step: z.number().nullable(),
    variant: z.number().nullable(),
    mailbox: z.string().nullable(),
  }),
]);
export type TimelineItem = z.infer<typeof timelineItemSchema>;

export const timelineResponseSchema = z.object({
  email: z.string(),
  leads: z.array(
    z.object({
      id: z.uuid(),
      tenantSlug: z.string(),
      company: z.string().nullable(),
      url: z.string().nullable(),
      status: z.string(),
      outreachStatus: z.string().nullable(),
      qualified: z.boolean().nullable(),
      score: z.number().nullable(),
    }),
  ),
  items: z.array(timelineItemSchema),
});
export type TimelineResponse = z.infer<typeof timelineResponseSchema>;

// ------------------------------------------------------------------ Operaciones

export const opsRunsQuerySchema = z.object({
  source: z.enum(['n8n', 'cron', 'worker']).optional(),
  status: opsRunStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export const opsRunDtoSchema = z.object({
  id: z.uuid(),
  tenantSlug: z.string().nullable(),
  source: z.enum(['n8n', 'cron', 'worker']),
  instance: z.string(),
  job: z.string(),
  status: opsRunStatusSchema,
  startedAt: isoDate,
  finishedAt: isoDate.nullable(),
  durationMs: z.number().nullable(),
  error: z.string().nullable(),
});
export type OpsRunDto = z.infer<typeof opsRunDtoSchema>;

export const heartbeatDtoSchema = z.object({
  id: z.uuid(),
  tenantSlug: z.string().nullable(),
  jobKey: z.string(),
  expectedIntervalSeconds: z.number(),
  graceSeconds: z.number(),
  lastSeenAt: isoDate.nullable(),
  lastStatus: z.enum(['success', 'error', 'warning']).nullable(),
  lastDurationMs: z.number().nullable(),
  overdue: z.boolean(),
});
export type HeartbeatDto = z.infer<typeof heartbeatDtoSchema>;

export const opsResponseSchema = z.object({
  runs: z.array(opsRunDtoSchema),
  heartbeats: z.array(heartbeatDtoSchema),
  ingest: z.object({
    pendingRaw: z.number(),
    failedRaw: z.number(),
    lastEventAt: isoDate.nullable(),
  }),
});
export type OpsResponse = z.infer<typeof opsResponseSchema>;

// ------------------------------------------------------------------ Alertas

export const ALERT_SEVERITIES = ['info', 'warning', 'critical'] as const;
export const alertSeveritySchema = z.enum(ALERT_SEVERITIES);

export const alertsQuerySchema = z.object({
  status: z.enum(['open', 'resolved']).default('open'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

export const alertDtoSchema = z.object({
  id: z.uuid(),
  tenantSlug: z.string().nullable(),
  ruleKey: z.string(),
  severity: alertSeveritySchema,
  title: z.string(),
  status: z.enum(['open', 'resolved']),
  occurrences: z.number(),
  openedAt: isoDate,
  lastSeenAt: isoDate,
  resolvedAt: isoDate.nullable(),
  notifiedAt: isoDate.nullable(),
  details: z.record(z.string(), z.unknown()),
});
export type AlertDto = z.infer<typeof alertDtoSchema>;

// ------------------------------------------------------------------ Config: conexiones de Instantly

export const campaignDtoSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  externalId: z.string(),
  name: z.string(),
});
export type CampaignDto = z.infer<typeof campaignDtoSchema>;

export const instantlyConnectionSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  label: z.string(),
  workspaceId: z.string().nullable(),
  hasApiKey: z.boolean(),
  apiKeyLast4: z.string().nullable(),
  webhookId: z.string().nullable(),
  status: z.enum(['active', 'disabled']),
  isShared: z.boolean(),
  autoResumeWebhooks: z.boolean(),
  rateBudgetPerMinute: z.number(),
  emailsBudgetPerMinute: z.number(),
  lastReconciledAt: isoDate.nullable(),
  lastEventAt: isoDate.nullable(),
  /** Path relativo del webhook: `/api/ingest/instantly/<id>` (se antepone APP_ORIGIN). */
  webhookPath: z.string(),
  /**
   * Campañas visibles para quien pidió la lista: TODAS si es la empresa dueña de la conexión, sólo
   * las propias si la ve por ser usuaria de una conexión compartida de otra empresa.
   */
  campaigns: z.array(campaignDtoSchema),
  /** false = conexión compartida de OTRA empresa, visible sólo porque una campaña propia la usa. */
  isOwner: z.boolean(),
  /** Nombre de la empresa dueña (para "Conexión compartida · Workspace de <ownerTenantName>"). */
  ownerTenantName: z.string(),
});
export type InstantlyConnection = z.infer<typeof instantlyConnectionSchema>;

export const createInstantlyConnectionSchema = z.object({
  label: z.string().trim().min(1).max(120),
  apiKey: z.string().min(10).max(500).optional(),
  workspaceId: z.string().trim().max(100).optional(),
  isShared: z.boolean().default(false),
  autoResumeWebhooks: z.boolean().default(false),
  rateBudgetPerMinute: z.number().int().min(1).max(6000).default(600),
});
export type CreateInstantlyConnection = z.input<typeof createInstantlyConnectionSchema>;

/** Respuesta de alta/rotación: el secreto del webhook se muestra UNA vez. */
export const instantlyConnectionSecretResponseSchema = z.object({
  connection: instantlyConnectionSchema,
  webhookSecret: z.string(),
  webhookUrl: z.string(),
});
export type InstantlyConnectionSecretResponse = z.infer<
  typeof instantlyConnectionSecretResponseSchema
>;

export const mapCampaignSchema = z.object({
  externalId: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(255),
  /** Empresa dueña de la campaña (conexión compartida). Default: la dueña de la conexión. */
  tenantId: z.uuid().optional(),
});
export type MapCampaign = z.input<typeof mapCampaignSchema>;

// ------------------------------------------------------------------ Config: API keys de ingesta

export const API_KEY_SCOPES = ['ingest'] as const;

export const apiKeyDtoSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid().nullable(),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(z.string()),
  lastUsedAt: isoDate.nullable(),
  revokedAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type ApiKeyDto = z.infer<typeof apiKeyDtoSchema>;

export const createApiKeySchema = z.object({
  name: z.string().trim().min(1).max(120),
  scopes: z.array(z.enum(API_KEY_SCOPES)).min(1).default(['ingest']),
});
export type CreateApiKey = z.input<typeof createApiKeySchema>;

export const createApiKeyResponseSchema = z.object({
  apiKey: z.string(),
  key: apiKeyDtoSchema,
});
